import { createClient } from '@supabase/supabase-js';

// Use environment variables for Supabase configuration
// The URL format should be: https://[project-id].supabase.co
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://pjgdijqhhsvkaarzdned.supabase.co';

// Use the publishable key for client-side applications
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'sb_publishable_Vwt_ntK4mSuZxbqBNSEKgw_BOlr0OQk';

// Check if API key is properly configured
if (!supabasePublishableKey || supabasePublishableKey === 'YOUR_ACTUAL_PUBLISHABLE_KEY_HERE') {
  console.error('❌ INVALID API KEY: Please set your actual Supabase publishable key');
  console.error('Go to your Supabase project → Settings → API → Project API keys → Copy the "publishable" key');
  console.error('Then either:');
  console.error('1. Set VITE_SUPABASE_PUBLISHABLE_KEY in your .env file, or');
  console.error('2. Replace the fallback value in this file');
}

// Validate URL format
if (!supabaseUrl.includes('.supabase.co')) {
  console.error('Invalid Supabase URL format:', supabaseUrl);
}

// Lazy initialization of Supabase client to avoid errors in sandboxed contexts
let supabaseInstance: ReturnType<typeof createClient> | null = null;

// Check if we're on a public card page (no auth needed)
const isPublicCardPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/card/');

// Create Supabase client lazily - only when needed (not on public pages)
const getSupabaseClient = (): ReturnType<typeof createClient> => {
  // On public card pages, return a minimal stub client that won't initialize auth
  if (isPublicCardPage) {
    if (!supabaseInstance) {
      supabaseInstance = createClient(supabaseUrl, supabasePublishableKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
          storage: undefined, // Explicitly disable storage
          flowType: 'pkce'
        },
        global: {
          headers: {
            'X-Client-Info': 'email-signature-generator'
          }
        },
        // Disable realtime and other features that might use LockManager
        realtime: {
          params: {
            eventsPerSecond: 10
          }
        }
      });
    }
    return supabaseInstance;
  }

  // For authenticated pages, create client normally but catch errors
  if (!supabaseInstance) {
    try {
      supabaseInstance = createClient(supabaseUrl, supabasePublishableKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
          storage: typeof window !== 'undefined' && 'localStorage' in window ? window.localStorage : undefined
        },
        global: {
          headers: {
            'X-Client-Info': 'email-signature-generator'
          }
        }
      });
    } catch (error) {
      console.warn('Supabase client initialization failed (may be in sandboxed context):', error);
      // Create a minimal client that will fail gracefully
      supabaseInstance = createClient(supabaseUrl, supabasePublishableKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
          storage: undefined // Disable storage for sandboxed contexts
        },
        global: {
          headers: {
            'X-Client-Info': 'email-signature-generator'
          }
        }
      });
    }
  }
  return supabaseInstance;
};

// Export a getter that lazily initializes
export const supabase = new Proxy({} as ReturnType<typeof createClient>, {
  get(_target, prop) {
    return getSupabaseClient()[prop as keyof ReturnType<typeof createClient>];
  }
});

// Function to get authenticated Supabase client with Clerk JWT
export const getAuthenticatedSupabase = async (getToken: () => Promise<string | null>) => {
  console.log('🔍 getAuthenticatedSupabase called');
  try {
    // CRITICAL FIX: Use Clerk.session.getToken() directly with template parameter
    // The getToken wrapper doesn't support template parameters
    console.log('🔍 Requesting JWT token with template: "supabase"');
    
    let token: string | null = null;
    
    // Try to get token with template using Clerk directly
    try {
      if (typeof window !== 'undefined' && (window as any).Clerk?.session) {
        console.log('🔍 Using Clerk.session.getToken() directly with template');
        token = await (window as any).Clerk.session.getToken({ template: 'supabase' });
        console.log('🔍 Token requested with template "supabase", got token:', token ? 'YES (length: ' + token.length + ')' : 'NO');
      } else {
        console.warn('⚠️ Clerk not available, trying getToken wrapper');
        // Fallback: try the wrapper (won't work with template but let's see what happens)
        try {
          token = await (getToken as any)({ template: 'supabase' });
        } catch {
          token = await getToken();
        }
      }
    } catch (templateError: any) {
      console.error('❌ ERROR requesting token with template:', templateError);
      console.error('❌ This means Clerk could not find template "supabase"');
      console.error('❌ Falling back to default token (will be RS256)');
      try {
        token = await getToken();
      } catch (fallbackError) {
        console.error('❌ Fallback token request also failed:', fallbackError);
      }
    }
    
    if (!token) {
      throw new Error('No Clerk token available');
    }

    // Debug: Log the token (first 50 chars for security) and decode header
    console.log('🔍 Clerk JWT token received:', token.substring(0, 50) + '...');
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const header = JSON.parse(atob(tokenParts[0]));
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('🔍 JWT Header:', header);
        console.log('🔍 JWT Payload (first 200 chars):', JSON.stringify(payload).substring(0, 200));
        console.log('🔍 JWT Algorithm:', header.alg);
        console.log('🔍 JWT Subject (user ID):', payload.sub);
        console.log('🔍 JWT Audience:', payload.aud);
        console.log('🔍 JWT Issuer:', payload.iss);
        
        // CRITICAL DEBUG: Check if algorithm matches expected HS256
        if (header.alg === 'RS256') {
          console.error('⚠️⚠️⚠️ CRITICAL: JWT is using RS256 but template should be HS256!');
          console.error('⚠️ Possible causes:');
          console.error('  1. Template "supabase" is not configured as HS256 in Clerk dashboard');
          console.error('  2. Multiple templates exist - wrong one is being used');
          console.error('  3. Template changes not saved/deployed');
          console.error('  4. Token cached from before template change - sign out/in to refresh');
          console.error('  5. Template name mismatch (case-sensitive? extra spaces?)');
          console.error('⚠️ ACTION: Check Clerk dashboard → JWT Templates → "supabase" → Algorithm should be HS256');
        } else if (header.alg === 'HS256') {
          console.log('✅ Algorithm is correct: HS256');
        }
        
        // Check audience
        if (!payload.aud || payload.aud !== 'authenticated') {
          console.error('⚠️⚠️⚠️ CRITICAL: JWT audience is missing or incorrect!');
          console.error('⚠️ Expected: "authenticated", Got:', payload.aud);
          console.error('⚠️ ACTION: Check Clerk template claims - must include "aud": "authenticated"');
        } else {
          console.log('✅ Audience is correct: authenticated');
        }
      }
    } catch (decodeErr) {
      console.warn('🔍 Could not decode JWT for debugging:', decodeErr);
    }

    // Create a new client with the Clerk JWT token
    // Use the method that works for both uploads AND saved previews
    const authenticatedSupabase = createClient(supabaseUrl, supabasePublishableKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false
      },
      global: {
        headers: {
          'X-Client-Info': 'email-signature-generator',
          'Authorization': `Bearer ${token}`
        }
      }
    });

    console.log('🔍 Authenticated Supabase client created with Clerk JWT');
    
    // Test if the JWT is working by calling a function
    console.log('🔍 Testing JWT authentication...');
    
    // Test if the JWT is working by calling a function
    try {
      const { data: testClaims, error: testError } = await authenticatedSupabase.rpc('get_jwt_claims');
      if (testError) {
        console.error('❌ JWT verification failed:', {
          code: testError.code,
          message: testError.message,
          details: testError.details,
          hint: testError.hint
        });
        console.error('❌ This means Supabase cannot verify the Clerk JWT token.');
        console.error('❌ Check: 1) JWT template name is "supabase" 2) Signing key matches Supabase JWT Signing Key 3) Algorithm is HS256');
      } else {
        console.log('✅ JWT verification successful:', testClaims);
      }
    } catch (testErr) {
      console.error('❌ JWT test exception:', testErr instanceof Error ? testErr.message : String(testErr));
    }
    
    return authenticatedSupabase;
  } catch (error) {
    console.error('Error getting authenticated Supabase client:', error);
    throw error;
  }
};

// Function to create Supabase session for Clerk user
export const createSupabaseSessionForClerkUser = async (clerkToken: string) => {
  try {
    const { data, error } = await supabase.auth.setSession({
      access_token: clerkToken,
      refresh_token: clerkToken
    });
    
    if (error) {
      console.error('Error creating Supabase session for Clerk user:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in createSupabaseSessionForClerkUser:', error);
    throw error;
  }
};

// Function to get current user
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Function to sign in
export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

// Function to sign up
export const signUp = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

// Function to convert Clerk user ID to UUID format
export const clerkUserIdToUUID = (clerkUserId: string): string => {
  // Remove the 'user_' prefix and convert to a valid UUID
  const userIdWithoutPrefix = clerkUserId.replace('user_', '');
  
  // Ensure it's exactly 32 characters by padding or truncating
  let paddedId = userIdWithoutPrefix.padEnd(32, '0').substring(0, 32);
  
  // Convert to UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  return `${paddedId.substring(0, 8)}-${paddedId.substring(8, 12)}-${paddedId.substring(12, 16)}-${paddedId.substring(16, 20)}-${paddedId.substring(20, 32)}`;
};
